import { useState } from 'react'
import { BRANDS, BRAND_INFO } from '@hub/shared'
import { BrandDot, typeLabel } from '../components/Brand.js'
import { PageHeader } from '../components/PageHeader.js'
import { addDays, short, today } from '../lib/dates.js'
import { useFilters, useSheetParam } from '../lib/filters.js'
import { METRIC_LABEL, formatMetric, usePerformance } from '../lib/performance.js'
import type { PerformanceRow } from '../lib/performance.js'

const RANGES = [
  { id: 30, label: '30 Tage' },
  { id: 90, label: '90 Tage' },
  { id: 365, label: '12 Monate' },
] as const

const avg = (values: number[]): number | undefined => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : undefined)

function pick(row: PerformanceRow, key: string): number | undefined {
  for (const s of row.sources) if (s.metrics[key] !== undefined) return s.metrics[key]
  return undefined
}

/** How what went out did — newsletters by open and click rate, posts by reach and saves. */
export function Performance(): JSX.Element {
  const [days, setDays] = useState<(typeof RANGES)[number]['id']>(90)
  const { filters } = useFilters()
  const { data = [], isLoading } = usePerformance(filters.brand, addDays(today(), -days))
  const { openItem } = useSheetParam()

  const measured = data.filter((r) => r.sources.length > 0)
  const mails = measured.filter((r) => pick(r, 'openRate') !== undefined)
  const posts = measured.filter((r) => pick(r, 'reach') !== undefined || pick(r, 'views') !== undefined)

  const tiles = [
    { label: 'veröffentlicht', value: String(data.length) },
    { label: 'mit Zahlen', value: String(measured.length) },
    { label: 'Ø Öffnungsrate', value: avg(mails.map((r) => pick(r, 'openRate')!)) },
    { label: 'Ø Klickrate', value: avg(mails.map((r) => pick(r, 'clickRate') ?? 0)) },
  ]

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-5 py-6 md:px-8">
      <PageHeader
        title="Performance"
        subtitle="Veröffentlichte Einträge mit ihren Zahlen"
        action={
          <div className="flex items-center rounded-full bg-cell p-1">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setDays(r.id)}
                className={`h-7 rounded-full px-3 text-small ${days === r.id ? 'bg-label font-medium text-cell' : 'text-label2/70'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="card">
            <p className="num font-brand text-[2.25rem] font-light leading-none">
              {typeof t.value === 'number' ? formatMetric('openRate', t.value) : (t.value ?? '–')}
            </p>
            <p className="mt-2 text-small text-label2/60">{t.label}</p>
          </div>
        ))}
      </div>

      {/* Per brand: how much went out, how much of it is measured */}
      <div className="grid gap-3 md:grid-cols-3">
        {BRANDS.filter((b) => !filters.brand.length || filters.brand.includes(b)).map((b) => {
          const mine = data.filter((r) => r.brand === b)
          const rate = avg(mails.filter((r) => r.brand === b).map((r) => pick(r, 'clickRate') ?? 0))
          return (
            <div key={b} className="card">
              <p className="flex items-center gap-2 text-small font-semibold">
                <BrandDot brand={b} /> {BRAND_INFO[b].name}
              </p>
              <p className="mt-2 text-small text-label2/70">
                <span className="num font-semibold text-label">{mine.length}</span> veröffentlicht ·{' '}
                <span className="num font-semibold text-label">{mine.filter((r) => r.sources.length).length}</span> mit Zahlen
                {rate !== undefined && (
                  <>
                    {' '}
                    · Ø Klickrate <span className="num font-semibold text-label">{formatMetric('clickRate', rate)}</span>
                  </>
                )}
              </p>
            </div>
          )
        })}
      </div>

      {measured.length > 0 && (
        <section>
          <p className="section-title px-0">Ergebnisse</p>
          <div className="inset-group">
            {measured.map((r) => {
              const keys = ['openRate', 'clickRate', 'reach', 'views', 'likes', 'saves'].filter((k) => pick(r, k) !== undefined).slice(0, 3)
              return (
                <button key={r.id} type="button" onClick={() => openItem(r.id)} className="list-row flex items-center gap-4">
                  <BrandDot brand={r.brand} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-callout font-semibold">{r.title}</p>
                    <p className="text-caption text-label2/60">
                      {short(r.start)} · {typeLabel(r.type)}
                    </p>
                  </div>
                  <div className="flex gap-5">
                    {keys.map((k) => (
                      <div key={k} className="hidden text-right sm:block">
                        <p className="num text-callout font-semibold">{formatMetric(k, pick(r, k)!)}</p>
                        <p className="text-caption text-label2/55">{METRIC_LABEL[k]}</p>
                      </div>
                    ))}
                    {keys[0] && (
                      <p className="num text-callout font-semibold sm:hidden">{formatMetric(keys[0], pick(r, keys[0])!)}</p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </section>
      )}

      {!isLoading && measured.length === 0 && (
        <div className="card">
          <p className="text-body">Noch keine Zahlen.</p>
          <p className="mt-1 text-small text-label2/65">
            Zahlen hängen an der Verknüpfung eines Eintrags (Brevo-Kampagne, Post). Sag Claude zum Beispiel: „Trag die Brevo-Zahlen der letzten
            Newsletter in den Marketing Hub ein.“
          </p>
        </div>
      )}
      {posts.length === 0 && mails.length > 0 && <p className="text-caption text-label2/50">Social-Zahlen fehlen noch – Claude kann sie aus Upload-Post eintragen.</p>}
    </div>
  )
}
